import React from 'react';
import { EMPTY_METRIC_ID, useExperimentsStore } from 'apps/experiments/src/store';
import { ExperimentMetric } from 'apps/experiments/src/types';
import { Directions } from 'common/components/drop-menu/types';
import { useExperimentPageContext } from 'apps/experiments/src/components/context';
import { useBehaviors } from 'common/contexts/react/behaviors-context';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { useMetrics } from 'common/contexts/react/metrics-resource-context';
import { useTranslation } from 'common/i18n/hooks';
import { IncludeCustomPropertyType } from 'common/query-builder/query-block/properties-menu/types';
import { QueryBuilderReact } from 'common/query-builder/query-builder';
import { getFilterSection, getPropertyFilters } from 'common/report/insights/util/filters';
import { FilterClause } from 'common/report/insights/models/filter-clause';
import { FilterClause as FilterClauseType } from 'common/report/insights/util/types';
import { QueryBuilderUpdateDetail } from 'common/query-builder/query-builder/types';
import { FiltersOperator, MetricEntry, QueryFilterEntry, QueryMetricEntry, QueryMultiMetricEntry, RowLabelTheme, RowLabelType, SelectorTypeDefinitions } from 'common/query-builder/query-entry-section/types';
import { MetricDefinition } from 'common/react-query/metrics/types';
import { FeatureGate, ReportName } from 'common/report/constants';
import { INSIGHTS_DEFAULT_METRIC } from 'common/report/insights/models/report';
import { hydrateSavedBehaviors, isBehaviorShowClause, isFormulaShowClause, isSavedMetricShowClause } from 'common/report/insights/models/utils';
import { getEntries, getShowSection } from 'common/report/insights/util/events';
import { CurrentProject } from 'common/report/model-resources/projects';
import { ChartType, MetricType } from 'common/types/reports/bookmark';
import { RollingWindowSize } from 'common/util';
import { DateRange } from 'common/widgets/date-range-picker/models/date-range';
import { getMetricName } from 'common/query-builder/query-entry-section/row-types/metric-entry/metrics/metric-entry-util';
import { TimeUnit } from 'common/widgets/date-range-picker/models/types';
import { getOtherAggregationOptions, getOtherFilterOptions } from 'common/report/insights/query-builder/utils';
import { ShowClause } from 'common/report/insights/models/show-clause';
import { BookmarkType } from 'common/report/queries/app-api/bookmark-client';
import { ReactErrorBoundary } from 'common/components/react-error-boundary';
import { ButtonReact } from 'common/components/button';
import { ButtonTheme } from 'common/components/button/constants';
import { ApplyParamsFunction, ReportBookmarkManager, ReportBookmarkManagerProvider } from 'common/report/insights/contexts/report-bookmark-manager';
import { TrackingEventSource } from 'apps/experiments/src/components/tracking';
import { transformToGlobalQueryFilterEntries } from 'common/query-builder/query-builder/util';
import { useExperimentMetrics } from 'iron/apps/experiments/src/components/hooks/metrics';
import { convertExperimentMetricToInsightsBookmarkParams } from 'iron/apps/experiments/src/components/utils';
import { usePrevious } from 'common/hooks/usePrevious';
import { useStagedExperiment } from 'iron/apps/experiments/src/components/hooks/staged-experiment';
import styles from './_metric-modal.module.styl';
export function ExperimentPageSummaryMetricsModal() {
  var _currentProjectResour, _currentProjectResour2, _currentMetric$name, _reportBookmarkManage, _reportBookmarkManage2;
  const {
    projectId,
    userId,
    workspaceId
  } = useExperimentPageContext();
  const {
    stagedExperiment: experiment
  } = useStagedExperiment();
  const currentMetric = useExperimentsStore(state => state.currentMetric);
  const {
    updateMetric,
    unselectMetric
  } = useExperimentMetrics();
  const behaviors = useBehaviors();
  const metrics = useMetrics();
  const currentProjectResource = useCurrentProject();
  const currentProject: CurrentProject | null = (_currentProjectResour = (_currentProjectResour2 = currentProjectResource.get()) === null || _currentProjectResour2 === void 0 ? void 0 : _currentProjectResour2.value) !== null && _currentProjectResour !== void 0 ? _currentProjectResour : null;
  const i18n = useTranslation();
  const previousMetricId = usePrevious(currentMetric === null || currentMetric === void 0 ? void 0 : currentMetric.id);
  const [isReopening, setIsReopening] = React.useState(false);

  // force the query-builder to re-initialize when the metric changes so the metrics popover opens
  React.useEffect(() => {
    // no change, don't refresh
    if ((currentMetric === null || currentMetric === void 0 ? void 0 : currentMetric.id) === previousMetricId) {
      return;
    }
    // don't refresh for existing metrics
    if ((currentMetric === null || currentMetric === void 0 ? void 0 : currentMetric.id) !== EMPTY_METRIC_ID) {
      return;
    }
    setIsReopening(true);
    setTimeout(() => {
      setIsReopening(false);
    });
  }, [currentMetric === null || currentMetric === void 0 ? void 0 : currentMetric.id, previousMetricId]);
  if (!currentMetric || isReopening) {
    return null;
  }
  const dateRange = new DateRange({
    from: (experiment === null || experiment === void 0 ? void 0 : experiment.start_date) || undefined,
    to: (experiment === null || experiment === void 0 ? void 0 : experiment.end_date) || undefined
  });
  const metricName: string = (_currentMetric$name = currentMetric.name) !== null && _currentMetric$name !== void 0 ? _currentMetric$name : "";
  let metricType: MetricType | null = null;

  // hydrate metric definition with saved behaviors
  const metricDefinition = currentMetric.definition ? hydrateSavedBehaviors(currentMetric.definition, behaviors) : undefined;
  if (metricDefinition) {
    if (isSavedMetricShowClause(metricDefinition)) {
      metricType = MetricType.SavedMetric;
    } else if (isFormulaShowClause(metricDefinition)) {
      metricType = MetricType.Formula;
    } else if (isBehaviorShowClause(metricDefinition)) {
      var _metricDefinition$beh;
      metricType = ((_metricDefinition$beh = metricDefinition.behavior) === null || _metricDefinition$beh === void 0 ? void 0 : _metricDefinition$beh.type) || null;
    }
  }
  const currentEntry = metricDefinition ? getEntries({
    showClauses: [metricDefinition],
    behaviorsResource: behaviors,
    metricsResource: metrics,
    dateUnit: TimeUnit.Day
  })[0] as QueryMetricEntry | MetricEntry : null;
  const maxHistoryDays = (currentProject === null || currentProject === void 0 ? void 0 : currentProject.getFeatureGate(FeatureGate.MaxDataHistoryDays)) || 365 * 5;
  const extractUpdatedMetricName = (updatedMetricDefinition?: MetricDefinition) => {
    let updatedMetricName = metricName;
    if (updatedMetricDefinition) {
      updatedMetricName = getMetricName({
        showClause: updatedMetricDefinition,
        metricsResource: metrics
      });
    }
    return updatedMetricName;
  };
  const handleFirstClassMetricQBUpdate: ApplyParamsFunction = params => {
    var _params$sections$glob;
    if (!currentMetric || !experiment) {
      return;
    }
    const updatedMetricDefinition: MetricDefinition = params.sections.show[0];
    const updatedMetricFilters = params.sections.filter as FilterClauseType[];
    const updatedDataGroupId = (_params$sections$glob = params.sections.globalDataGroupId) !== null && _params$sections$glob !== void 0 ? _params$sections$glob : "";
    const updatedMetricName = extractUpdatedMetricName(updatedMetricDefinition);
    const metricUpdates: Partial<ExperimentMetric> = {
      definition: updatedMetricDefinition,
      filters: updatedMetricFilters,
      dataGroupId: updatedDataGroupId
    };
    if (updatedMetricName && updatedMetricName !== metricName) {
      metricUpdates.name = updatedMetricName;
    }
    updateMetric(metricUpdates);
  };
  const handleQueryBuilderUpdate = (updates: QueryBuilderUpdateDetail) => {
    var _updates$filters;
    if (!currentMetric || !experiment) {
      return;
    }
    const delta = updates.delta;
    const hasUpdatedMetricDefinition = !!delta.entries;
    const hasUpdatedMetricFilters = !!delta.filters;
    const hasUpdatedDataGroupId = delta.dataGroupId !== undefined;
    const updatedMetricDefinition: MetricDefinition = getShowSection({
      entries: updates.entries ? updates.entries as Array<QueryMetricEntry | MetricEntry> : []
    }).toBookmarkData()[0] || null;
    const updatedMetricName = extractUpdatedMetricName(updatedMetricDefinition);
    const updatedMetricFilters: FilterClauseType[] = getFilterSection((_updates$filters = updates.filters) !== null && _updates$filters !== void 0 ? _updates$filters : [], FiltersOperator.And).toBookmarkData();
    const metricUpdates: Partial<ExperimentMetric> = {};
    if (hasUpdatedMetricDefinition) {
      metricUpdates.definition = updatedMetricDefinition;
    }
    if (hasUpdatedDataGroupId) {
      metricUpdates.dataGroupId = delta.dataGroupId || "";
    }
    if (updatedMetricName !== metricName) {
      metricUpdates.name = updatedMetricName;
    }
    if (hasUpdatedMetricFilters) {
      metricUpdates.filters = updatedMetricFilters;
    }
    updateMetric(metricUpdates);
  };
  if (!currentMetric) {
    return null;
  }
  const currentFilterEntries: QueryFilterEntry[] = currentMetric.filters ? transformToGlobalQueryFilterEntries(getPropertyFilters(currentMetric.filters.map(filter => new FilterClause(filter)))) : [];
  const reportBookmarkManager = new ReportBookmarkManager();
  // we don't care much about on update event here
  reportBookmarkManager.setup(handleFirstClassMetricQBUpdate, {
    metrics,
    behaviors,
    i18n,
    currentProject: currentProjectResource
  });
  reportBookmarkManager.syncBookmark({
    type: BookmarkType.Insights,
    params: convertExperimentMetricToInsightsBookmarkParams({
      experimentMetric: currentMetric,
      overrideChartType: ChartType.Metric
    }),
    name: "tmp"
  });
  return <div className={styles.wrapper} onClick={ev =>
  // Otherwise clicks inside the modal will propagate to the page and close the modal
  ev.stopPropagation()} data-sentry-component="ExperimentPageSummaryMetricsModal" data-sentry-source-file="_metric-modal.tsx">
      <div className={styles.modalHeader}>
        <div>{i18n.t("experiments.metrics-modal.title")}</div>
        <ButtonReact icon='x' theme={ButtonTheme.Tertiary} onClick={() => unselectMetric(TrackingEventSource.ExperimentPageMetricsModal)} data-sentry-element="ButtonReact" data-sentry-source-file="_metric-modal.tsx" />
      </div>
      <div className={styles.modalBody}>
        <ReactErrorBoundary data-sentry-element="ReactErrorBoundary" data-sentry-source-file="_metric-modal.tsx">
          {/* This is a hacky way to make first class metric to work with the experiments qb */}
          {/* seems like first class metric assumes qb takes in a bookmark but experiments metrics have a subset of bookmark */}
          <ReportBookmarkManagerProvider value={reportBookmarkManager} data-sentry-element="ReportBookmarkManagerProvider" data-sentry-source-file="_metric-modal.tsx">
            <QueryBuilderReact queryBuilderContextValue={{
            isCoreReportQb: false,
            qbAlignment: Directions.Right
          }} addEntryButtonTooltipText={i18n.t("query-builder.add-metric")} allDataGroups={currentProject === null || currentProject === void 0 ? void 0 : currentProject.canUseDataGroups()} className={styles.queryBuilder} dataGroupId={currentMetric === null || currentMetric === void 0 ? void 0 : currentMetric.dataGroupId} dataGroupsEnabled={currentProject === null || currentProject === void 0 ? void 0 : currentProject.canUseDataGroups()} dateRange={dateRange} dateUnit={RollingWindowSize.Day} disableAddEntry={!!currentEntry} disableAddEntryReason={currentEntry ? i18n.t("experiments.metrics-modal.one-metric") : ""} disableBreakdown disableCloneEntry
          // TODO[EF] - resolve saved metrics and check if one of the referenced metrics has people
          disableProfileHistory={metricType === MetricType.People} enableEnhancedListFilters
          // TODO[EF] - resolve saved metrics and check if one of the referenced metrics has funnel
          enableFilterAttribution={metricType === MetricType.Funnel} enableHideEntry entryRowLabelType={RowLabelType.Alpha} entryRowLabelTheme={RowLabelTheme.Gray} entries={currentEntry ? [currentEntry] : []} emptyPlaceholder={INSIGHTS_DEFAULT_METRIC as QueryMultiMetricEntry} filters={currentFilterEntries} hideDatePicker hideGroupBy includeCustomProperties={IncludeCustomPropertyType.All} maxDataHistoryDays={maxHistoryDays} metricsMode otherFilterOptions={(_reportBookmarkManage = reportBookmarkManager.bookmark) !== null && _reportBookmarkManage !== void 0 && _reportBookmarkManage.params ? getOtherFilterOptions({
            params: (_reportBookmarkManage2 = reportBookmarkManager.bookmark) === null || _reportBookmarkManage2 === void 0 ? void 0 : _reportBookmarkManage2.params,
            showClauses: metricDefinition ? [ShowClause.createShowClause(metricDefinition)] : [],
            hasGlobalDataGroup: false,
            insightsReportMode: BookmarkType.Insights,
            multiAttributionProperty: null,
            currentProject
          }) : []}
          // TODO[EF] - resolve saved metrics and check if one of the referenced metrics has event
          otherAggregationOptions={getOtherAggregationOptions({
            hasEventsQuery: metricType === MetricType.Event,
            insightsReportMode: BookmarkType.Insights
          })} projectAsOfDate={(currentProject === null || currentProject === void 0 ? void 0 : currentProject.projectAsOfDate) || undefined} projectHasCohorts={currentProject === null || currentProject === void 0 ? void 0 : currentProject.canUseCohorts()} projectHasPeople={currentProject === null || currentProject === void 0 ? void 0 : currentProject.hasIntegratedEngage} projectIsFreePlan={currentProject === null || currentProject === void 0 ? void 0 : currentProject.isFreeFeatureGateTier()} queryOrigin={"experiment"} reportName={ReportName.Insights} restrictTopEventsOnly={false} rowTypes={[SelectorTypeDefinitions.Metrics]} showBorrowedProperties showDataGroupsSelector={false} showEntryRename showSessions showTopEvents showSpecialDatasetProperty={currentProject === null || currentProject === void 0 ? void 0 : currentProject.isRollupProject()} stagingRow supportB2bCohorts={currentProject === null || currentProject === void 0 ? void 0 : currentProject.canUseDataGroups()} projectId={projectId} userId={userId} workspaceId={workspaceId} onUpdate={handleQueryBuilderUpdate} data-sentry-element="QueryBuilderReact" data-sentry-source-file="_metric-modal.tsx" />
          </ReportBookmarkManagerProvider>
        </ReactErrorBoundary>
      </div>
    </div>;
}